import React, { useState, useEffect, useCallback } from 'react';
import { Clock, CircleCheck as CheckCircle, Package, Truck, User, MapPin, CreditCard, Calendar, RefreshCw, TriangleAlert as AlertTriangle, ChefHat, Circle as XCircle, Chrome as Home, ShieldCheck, Loader, Search, ArrowUpDown, ExternalLink, X, Layers } from 'lucide-react';
import { trackFood, trackError } from '../Utils/analytics';
import { KITCHEN_ORDERS_V2_URL, KITCHEN_ORDER_DETAIL_URL, KITCHEN_BULK_ACTION_URL, ORDER_STATUS_UPDATE_URL, ORDER_MARK_DELIVERED_URL } from '../../config/api';
import { authenticatedFetch } from '../Utils/apiHelpers';
import { toDate } from '../Utils/time';
import { useOrderSocket, type OrderStatusEvent, type NewOrderEvent } from '../Utils/useOrderSocket';
import './MyKitchenOrdersTab.css';

interface OrderListItem {
  order_id: number;
  order_status: string;
  delivery_type: string;
  total_price: number;
  payment_method: string;
  payment_status: string;
  items_count: number;
  items_summary: string;
  customer: { name: string; phone: string | null };
  created_at: string;
  delivered_at: string | null;
}

interface OrderDetailItem {
  food_id: number;
  food_name: string;
  quantity: number;
  price: number;
  subtotal: number;
}

interface OrderDetailCustomer {
  user_id: number;
  name: string;
  phone: string | null;
  email: string;
}

interface OrderDetailDelivery {
  address: string;
  distance_km: number;
  distance_miles: number;
  eta: string | null;
  user_lat: number;
  user_lon: number;
  google_maps_directions: string;
}

interface TimelineEntry {
  status: string;
  message: string;
  timestamp: string;
}

interface AvailableAction {
  action: string;
  label: string;
  endpoint: string;
}

interface OrderDetail {
  order_id: number;
  order_status: string;
  delivery_type: string;
  payment_method: string;
  payment_status: string;
  subtotal: number;
  delivery_fee: number;
  tax_amount: number;
  total_price: number;
  created_at: string;
  delivered_at: string | null;
  otp_required: boolean;
  otp_attempts_used: number;
  otp_attempts_remaining: number;
}

interface OrderDetailResponse {
  success: boolean;
  order: OrderDetail;
  customer: OrderDetailCustomer;
  delivery: OrderDetailDelivery;
  items: OrderDetailItem[];
  timeline: TimelineEntry[];
  available_actions: AvailableAction[];
}

interface MyKitchenOrdersTabProps {
  kitchenId: number | null;
  showNotification: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
}

const statusOptions = [
  { value: 'confirmed', label: 'Confirmed', color: '#17a2b8', icon: CheckCircle },
  { value: 'preparing', label: 'Preparing', color: '#ffc107', icon: ChefHat },
  { value: 'ready', label: 'Ready', color: '#fd7e14', icon: Package },
  { value: 'out_for_delivery', label: 'Out for Delivery', color: '#6f42c1', icon: Truck },
  { value: 'delivered', label: 'Delivered', color: '#28a745', icon: Home },
  { value: 'cancelled', label: 'Cancelled', color: '#6c757d', icon: XCircle },
];

const TERMINAL_STATUSES = ['delivered', 'cancelled'];

const getStatusMeta = (status: string) => statusOptions.find(o => o.value === status.toLowerCase());
const getStatusColor = (status: string): string => getStatusMeta(status)?.color || '#6c757d';
const getStatusIcon = (status: string) => {
  const Icon = getStatusMeta(status)?.icon || Clock;
  return <Icon size={16} />;
};

const MyKitchenOrdersTab: React.FC<MyKitchenOrdersTabProps> = ({ kitchenId, showNotification }) => {
  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingOrderId, setUpdatingOrderId] = useState<number | null>(null);

  // Filters & search
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [deliveryTypeFilter, setDeliveryTypeFilter] = useState<string>('');
  const [paymentFilter, setPaymentFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<string>('desc');
  const [showFilters, setShowFilters] = useState(false);

  // Pagination
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [loadingMoreOrders, setLoadingMoreOrders] = useState(false);
  const ORDERS_PAGE_SIZE = 20;

  // Order detail modal
  const [detailOrderId, setDetailOrderId] = useState<number | null>(null);
  const [detailData, setDetailData] = useState<OrderDetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  // Mark-delivered (OTP verification) state
  const [markingDeliveredOrderId, setMarkingDeliveredOrderId] = useState<number | null>(null);
  const [otpInput, setOtpInput] = useState<string>('');
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  // Bulk action state
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<number>>(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  const fetchKitchenOrders = async (page: number = 1, append: boolean = false) => {
    if (!kitchenId) {
      setError('Kitchen ID not found. Please create a kitchen first.');
      setIsLoading(false);
      return;
    }

    if (!append) setIsLoading(true);
    else setLoadingMoreOrders(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(ORDERS_PAGE_SIZE));
      params.set('sort_by', sortBy);
      params.set('sort_order', sortOrder);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (deliveryTypeFilter) params.set('delivery_type', deliveryTypeFilter);
      if (paymentFilter) params.set('payment_method', paymentFilter);
      if (searchQuery.trim()) params.set('search', searchQuery.trim());

      const url = `${KITCHEN_ORDERS_V2_URL}?${params.toString()}`;
      const response = await authenticatedFetch(url, {
        method: 'GET',
        headers: { 'accept': 'application/json' },
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError('Authentication failed. Please log in again.');
        } else if (response.status === 403) {
          setError('You do not have permission to view orders.');
        } else if (response.status === 404) {
          setError('No orders found.');
        } else {
          setError('Failed to fetch orders. Please try again later.');
        }
        setIsLoading(false);
        return;
      }

      const json = await response.json();
      const allOrders: OrderListItem[] = json.orders || [];
      setOrdersTotal(json.total ?? allOrders.length);
      setHasNext(json.has_next ?? false);
      setOrders(prev => append ? [...prev, ...allOrders] : allOrders);

      if (!append && allOrders.length === 0) {
        setError('No orders found for your kitchen yet.');
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
      trackError('kitchen_orders_fetch_failed', 'kitchen_orders_tab');
    } finally {
      setIsLoading(false);
      setLoadingMoreOrders(false);
    }
  };

  const fetchOrderDetail = async (orderId: number) => {
    setDetailLoading(true);
    setDetailError(null);
    try {
      const res = await authenticatedFetch(KITCHEN_ORDER_DETAIL_URL(orderId), {
        method: 'GET',
        headers: { 'accept': 'application/json' },
      });
      if (res.ok) {
        const data: OrderDetailResponse = await res.json();
        setDetailData(data);
      } else if (res.status === 404) {
        setDetailError('Order not found.');
      } else {
        setDetailError('Failed to load order details.');
      }
    } catch {
      setDetailError('Network error. Please check your connection.');
    } finally {
      setDetailLoading(false);
    }
  };

  const openOrderDetail = (orderId: number) => {
    setDetailOrderId(orderId);
    setDetailData(null);
    setDetailError(null);
    fetchOrderDetail(orderId);
  };

  const closeOrderDetail = () => {
    setDetailOrderId(null);
    setDetailData(null);
    setDetailError(null);
  };

  const handleLoadMoreOrders = () => {
    const nextPage = ordersPage + 1;
    setOrdersPage(nextPage);
    fetchKitchenOrders(nextPage, true);
  };

  const applyFilters = () => {
    setOrdersPage(1);
    setSelectedOrderIds(new Set());
    fetchKitchenOrders(1);
  };

  const handleMarkDelivered = async (orderId: number) => {
    if (!otpInput.trim()) {
      showNotification('error', 'OTP Required', 'Please enter the 4-digit OTP shared by the customer.');
      return;
    }
    setVerifyingOtp(true);
    try {
      const response = await authenticatedFetch(ORDER_MARK_DELIVERED_URL(orderId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'accept': 'application/json' },
        body: JSON.stringify({ otp: otpInput.trim() }),
      });
      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        setOrders(prev => prev.map(o => o.order_id === orderId ? { ...o, order_status: 'delivered' } : o));
        setMarkingDeliveredOrderId(null);
        setOtpInput('');
        showNotification('success', 'Order Delivered', data.message || `Order #${orderId} marked as delivered.`);
        trackFood('food_updated', `order_${orderId}_delivered_otp`);
        if (detailOrderId === orderId) fetchOrderDetail(orderId);
      } else {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 400 || response.status === 422) {
          showNotification('error', 'Invalid OTP', errorData.message || 'The OTP entered is incorrect. Please ask the customer for the correct OTP.');
        } else if (response.status === 404) {
          showNotification('error', 'Order Not Found', 'The order could not be found.');
        } else if (response.status === 401) {
          showNotification('error', 'Authentication Failed', 'Please log in again.');
        } else {
          showNotification('error', 'Delivery Failed', errorData.message || 'Could not mark order as delivered.');
        }
        trackError('order_mark_delivered_failed', 'kitchen_orders_tab');
      }
    } catch {
      showNotification('error', 'Network Error', 'Please check your connection and try again.');
      trackError('order_mark_delivered_network_error', 'kitchen_orders_tab');
    } finally {
      setVerifyingOtp(false);
    }
  };

  const openDeliveredOtpInput = (orderId: number) => {
    setMarkingDeliveredOrderId(orderId);
    setOtpInput('');
  };

  const cancelDeliveredOtpInput = () => {
    setMarkingDeliveredOrderId(null);
    setOtpInput('');
  };

  const handleUpdateStatus = async (orderId: number, newStatus: string) => {
    setUpdatingOrderId(orderId);
    try {
      const response = await authenticatedFetch(`${ORDER_STATUS_UPDATE_URL(orderId)}?status=${newStatus}`, {
        method: 'POST',
        headers: { 'accept': 'application/json' },
      });
      if (response.ok) {
        setOrders(prev => prev.map(o => o.order_id === orderId ? { ...o, order_status: newStatus } : o));
        showNotification('success', 'Status Updated', `Order #${orderId} marked as ${newStatus}.`);
        trackFood('food_updated', `order_${orderId}_status_${newStatus}`);
        if (detailOrderId === orderId) fetchOrderDetail(orderId);
      } else {
        const errorData = await response.json().catch(() => ({}));
        showNotification('error', 'Update Failed', errorData.message || 'Failed to update order status.');
        trackError('order_status_update_failed', 'kitchen_orders_tab');
      }
    } catch {
      showNotification('error', 'Network Error', 'Please check your connection and try again.');
      trackError('order_status_update_network_error', 'kitchen_orders_tab');
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const handleActionEndpoint = async (action: AvailableAction, orderId: number) => {
    // The endpoint from available_actions is a relative path like "/api/v1/order/58/orderstatus?status=preparing"
    // We need to call it with the full base URL
    const apiBase = KITCHEN_ORDERS_V2_URL.replace('/api/v1/kitchen/orders', '');
    const fullUrl = `${apiBase}${action.endpoint}`;
    const isDeliveryResponse = action.endpoint.includes('/delivery-response');
    const requestOptions: RequestInit = isDeliveryResponse
      ? {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', 'accept': 'application/json' },
          body: JSON.stringify({
            accept: action.action !== 'decline_delivery',
            decline_reason: action.action === 'decline_delivery' ? 'Declined by kitchen' : '',
          }),
        }
      : {
          method: 'POST',
          headers: { 'accept': 'application/json' },
        };
    setUpdatingOrderId(orderId);
    try {
      const response = await authenticatedFetch(fullUrl, requestOptions);
      if (response.ok) {
        showNotification('success', 'Action Completed', `${action.label} for order #${orderId}.`);
        // Refresh both the list and detail
        fetchKitchenOrders(ordersPage);
        if (detailOrderId === orderId) fetchOrderDetail(orderId);
      } else {
        const errorData = await response.json().catch(() => ({}));
        showNotification('error', 'Action Failed', errorData.message || `Could not ${action.label.toLowerCase()}.`);
      }
    } catch {
      showNotification('error', 'Network Error', 'Please check your connection and try again.');
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const toggleOrderSelection = (orderId: number) => {
    setSelectedOrderIds(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const activeOrders = orders.filter(o => !TERMINAL_STATUSES.includes(o.order_status.toLowerCase()));
    if (selectedOrderIds.size === activeOrders.length && activeOrders.length > 0) {
      setSelectedOrderIds(new Set());
    } else {
      setSelectedOrderIds(new Set(activeOrders.map(o => o.order_id)));
    }
  };

  const handleBulkAction = async (action: string) => {
    if (selectedOrderIds.size === 0) {
      showNotification('warning', 'No Orders Selected', 'Please select at least one order.');
      return;
    }
    setBulkActionLoading(true);
    try {
      const response = await authenticatedFetch(KITCHEN_BULK_ACTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'accept': 'application/json' },
        body: JSON.stringify({
          order_ids: Array.from(selectedOrderIds),
          action: action,
        }),
      });
      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        showNotification('success', 'Bulk Action Complete', `${data.updated || selectedOrderIds.size} order(s) updated.${data.failed_count > 0 ? ` ${data.failed_count} failed.` : ''}`);
        setSelectedOrderIds(new Set());
        fetchKitchenOrders(ordersPage);
      } else {
        const errData = await response.json().catch(() => ({}));
        showNotification('error', 'Bulk Action Failed', errData.message || 'Could not update orders.');
      }
    } catch {
      showNotification('error', 'Network Error', 'Please check your connection and try again.');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    try {
      return toDate(dateString).toLocaleDateString('en-GB', {
        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch { return dateString; }
  };

  const formatTimelineDate = (dateString: string): string => {
    try {
      return toDate(dateString).toLocaleString('en-GB', {
        day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
      });
    } catch { return dateString; }
  };

  // Real-time updates via WebSocket
  const handleNewOrder = useCallback((event: NewOrderEvent) => {
    showNotification('info', 'New Order Received', `Order #${event.order_id} just came in.`);
    fetchKitchenOrders(1);
  }, [showNotification]);

  const handleOrderStatus = useCallback((event: OrderStatusEvent) => {
    setOrders(prev => prev.map(o => o.order_id === event.order_id ? { ...o, order_status: event.status } : o));
  }, []);

  useOrderSocket({ onNewOrder: handleNewOrder, onOrderStatus: handleOrderStatus });

  useEffect(() => {
    fetchKitchenOrders(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kitchenId]);

  if (!kitchenId) {
    return (
      <div className="user-profile-empty-state">
        <ChefHat size={60} />
        <p>Please create a kitchen first to view orders.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="user-profile-empty-state">
        <div className="user-profile-loading-spinner" style={{ width: '40px', height: '40px', marginBottom: '1rem' }}></div>
        <p>Loading your kitchen orders...</p>
      </div>
    );
  }

  if (error && orders.length === 0) {
    return (
      <div className="user-profile-empty-state">
        <AlertTriangle size={60} />
        <p>{error}</p>
        <button onClick={() => { setOrdersPage(1); fetchKitchenOrders(1); }} className="user-profile-edit-btn" style={{ marginTop: '1rem' }}>
          <RefreshCw size={16} />
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="kitchen-orders-container">
      <div className="kitchen-orders-header">
        <div className="orders-title-section">
          <h4>Kitchen Orders</h4>
          <p>Manage orders received for your kitchen</p>
        </div>
        <button onClick={() => { setOrdersPage(1); fetchKitchenOrders(1); }} className="user-profile-edit-btn" disabled={isLoading}>
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Search & Filter Bar */}
      <div className="orders-toolbar">
        <div className="orders-search-row">
          <div className="orders-search-box">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search by order ID or customer name..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') applyFilters(); }}
            />
          </div>
          <button className="orders-filter-toggle-btn" onClick={() => setShowFilters(!showFilters)}>
            <ArrowUpDown size={16} />
            Filters & Sort
          </button>
          <button className="user-profile-edit-btn" onClick={applyFilters}>
            <Search size={16} />
            Apply
          </button>
        </div>

        {showFilters && (
          <div className="orders-filters-panel">
            <div className="orders-filter-group">
              <label>Delivery Type</label>
              <select value={deliveryTypeFilter} onChange={e => setDeliveryTypeFilter(e.target.value)}>
                <option value="">All</option>
                <option value="self_pickup">Self Pickup</option>
                <option value="kitchen_delivery">Kitchen Delivery</option>
              </select>
            </div>
            <div className="orders-filter-group">
              <label>Payment Method</label>
              <select value={paymentFilter} onChange={e => setPaymentFilter(e.target.value)}>
                <option value="">All</option>
                <option value="cod">COD</option>
                <option value="online">Online</option>
              </select>
            </div>
            <div className="orders-filter-group">
              <label>Sort By</label>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
                <option value="created_at">Date</option>
                <option value="total_price">Total Price</option>
                <option value="order_id">Order ID</option>
              </select>
            </div>
            <div className="orders-filter-group">
              <label>Sort Order</label>
              <select value={sortOrder} onChange={e => setSortOrder(e.target.value)}>
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Status filter pills */}
      {orders.length > 0 && (
        <div className="orders-filter-bar">
          {['all', 'confirmed', 'preparing', 'ready', 'out_for_delivery', 'delivered', 'cancelled'].map(s => {
            const count = s === 'all' ? orders.length : orders.filter(o => o.order_status.toLowerCase() === s).length;
            if (s !== 'all' && count === 0) return null;
            const label = s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1).replace('_', ' ');
            return (
              <button
                key={s}
                className={`orders-filter-btn${statusFilter === s ? ' active' : ''}`}
                onClick={() => { setStatusFilter(s); setOrdersPage(1); setSelectedOrderIds(new Set()); fetchKitchenOrders(1); }}
              >
                {label}
                <span className="orders-filter-count">{count}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Bulk action bar */}
      {selectedOrderIds.size > 0 && (
        <div className="bulk-action-bar">
          <div className="bulk-action-info">
            <Layers size={16} />
            <span>{selectedOrderIds.size} order(s) selected</span>
          </div>
          <div className="bulk-action-buttons">
            <button
              className="bulk-action-btn bulk-action-btn--ready"
              onClick={() => handleBulkAction('mark_ready')}
              disabled={bulkActionLoading}
            >
              {bulkActionLoading ? <Loader size={14} className="spin" /> : <Package size={14} />}
              Mark All Ready
            </button>
            <button
              className="bulk-action-btn bulk-action-btn--preparing"
              onClick={() => handleBulkAction('start_preparing')}
              disabled={bulkActionLoading}
            >
              <ChefHat size={14} />
              Start Preparing
            </button>
            <button
              className="bulk-action-btn bulk-action-btn--cancel"
              onClick={() => setSelectedOrderIds(new Set())}
            >
              <X size={14} />
              Clear
            </button>
          </div>
        </div>
      )}

      {orders.length === 0 ? (
        <div className="user-profile-empty-state">
          <Package size={60} />
          <p>No orders received yet. Start promoting your kitchen!</p>
        </div>
      ) : (
        <>
        <div className="orders-list">
          {/* Select All checkbox row */}
          {orders.filter(o => !TERMINAL_STATUSES.includes(o.order_status.toLowerCase())).length > 0 && (
            <div className="select-all-row">
              <label className="select-all-label">
                <input
                  type="checkbox"
                  checked={selectedOrderIds.size === orders.filter(o => !TERMINAL_STATUSES.includes(o.order_status.toLowerCase())).length && selectedOrderIds.size > 0}
                  onChange={toggleSelectAll}
                />
                <span>Select all active orders</span>
              </label>
            </div>
          )}

          {orders.map(order => {
            const currentStatus = order.order_status.toLowerCase();
            const isTerminal = TERMINAL_STATUSES.includes(currentStatus);
            const isUpdating = updatingOrderId === order.order_id;
            const isSelected = selectedOrderIds.has(order.order_id);

            return (
              <div key={order.order_id} className={`order-card${isSelected ? ' order-card--selected' : ''}`}>
                <div className="order-header">
                  <div className="order-id-section">
                    {!isTerminal && (
                      <input
                        type="checkbox"
                        className="order-select-checkbox"
                        checked={isSelected}
                        onChange={() => toggleOrderSelection(order.order_id)}
                      />
                    )}
                    <h5>Order #{order.order_id}</h5>
                    <div className="order-status-badge" style={{ backgroundColor: getStatusColor(currentStatus), color: 'white' }}>
                      {getStatusIcon(currentStatus)}
                      <span>{currentStatus.replace('_', ' ').charAt(0).toUpperCase() + currentStatus.replace('_', ' ').slice(1)}</span>
                    </div>
                  </div>
                  <div className="order-date">
                    <Calendar size={14} />
                    <span>{formatDate(order.created_at)}</span>
                  </div>
                </div>

                <div className="order-details-grid">
                  <div className="order-detail">
                    <span className="detail-label">Items</span>
                    <span className="detail-value">{order.items_summary || `${order.items_count} item(s)`}</span>
                  </div>
                  <div className="order-detail">
                    <span className="detail-label">Total</span>
                    <span className="detail-value price">£{order.total_price.toFixed(2)}</span>
                  </div>
                  <div className="order-detail">
                    <span className="detail-label">Payment</span>
                    <span className="detail-value">
                      <CreditCard size={13} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                      {order.payment_method}
                      <span style={{ fontSize: '0.75rem', color: order.payment_status === 'paid' ? '#28a745' : '#6c757d', fontWeight: 500, marginLeft: '4px' }}>
                        ({order.payment_status})
                      </span>
                    </span>
                  </div>
                  <div className="order-detail">
                    <span className="detail-label">Customer</span>
                    <span className="detail-value">{order.customer.name}</span>
                  </div>
                </div>

                <div className="delivery-info1-section">
                  <div className="delivery-info1">
                    <div className="delivery-type">
                      {order.delivery_type === 'kitchen_delivery' ? <Truck size={16} /> : <User size={16} />}
                      <span>{order.delivery_type === 'kitchen_delivery' ? 'Kitchen Delivery' : 'Self Pickup'}</span>
                    </div>
                  </div>
                </div>

                {/* Quick actions */}
                {!isTerminal && (
                  <div className="order-actions">
                    <button
                      className="status-update-btn order-view-details-btn"
                      onClick={() => openOrderDetail(order.order_id)}
                      disabled={isUpdating}
                    >
                      <ExternalLink size={16} />
                      <span>View Details & Actions</span>
                    </button>
                    {currentStatus === 'confirmed' && (
                      <button
                        onClick={() => handleUpdateStatus(order.order_id, 'cancelled')}
                        disabled={isUpdating}
                        className="status-update-btn"
                        style={{ backgroundColor: '#dc3545', color: 'white' }}
                      >
                        {isUpdating ? <Loader size={16} className="spin" /> : <XCircle size={16} />}
                        <span>Cancel Order</span>
                      </button>
                    )}
                    {currentStatus === 'confirmed' && (
                      <button
                        onClick={() => handleUpdateStatus(order.order_id, 'preparing')}
                        disabled={isUpdating}
                        className="status-update-btn"
                        style={{ backgroundColor: getStatusColor('preparing'), color: 'white' }}
                      >
                        {isUpdating ? <Loader size={16} className="spin" /> : getStatusIcon('preparing')}
                        <span>Start Preparing</span>
                      </button>
                    )}
                    {currentStatus === 'preparing' && (
                      <button
                        onClick={() => handleUpdateStatus(order.order_id, 'ready')}
                        disabled={isUpdating}
                        className="status-update-btn"
                        style={{ backgroundColor: getStatusColor('ready'), color: 'white' }}
                      >
                        {isUpdating ? <Loader size={16} className="spin" /> : getStatusIcon('ready')}
                        <span>Mark as Ready</span>
                      </button>
                    )}
                    {currentStatus === 'ready' && (
                      <button
                        onClick={() => handleUpdateStatus(order.order_id, 'out_for_delivery')}
                        disabled={isUpdating}
                        className="status-update-btn"
                        style={{ backgroundColor: getStatusColor('out_for_delivery'), color: 'white' }}
                      >
                        {isUpdating ? <Loader size={16} className="spin" /> : getStatusIcon('out_for_delivery')}
                        <span>Out for Delivery</span>
                      </button>
                    )}
                    {currentStatus === 'out_for_delivery' && (
                      <button
                        onClick={() => openDeliveredOtpInput(order.order_id)}
                        disabled={isUpdating}
                        className="status-update-btn"
                        style={{ backgroundColor: getStatusColor('delivered'), color: 'white' }}
                      >
                        {isUpdating ? <Loader size={16} className="spin" /> : <ShieldCheck size={16} />}
                        <span>Mark as Delivered (OTP)</span>
                      </button>
                    )}
                  </div>
                )}

                {/* OTP verify inline */}
                {markingDeliveredOrderId === order.order_id && (
                  <div className="otp-verify-section">
                    <div className="otp-verify-header">
                      <ShieldCheck size={18} />
                      <span className="otp-verify-title">Verify Delivery OTP</span>
                    </div>
                    <p className="otp-verify-prompt">Ask the customer for their 4-digit OTP and enter it below to confirm delivery.</p>
                    <div className="otp-verify-body">
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={4}
                        className="otp-verify-input"
                        placeholder="••••"
                        value={otpInput}
                        onChange={e => setOtpInput(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        disabled={verifyingOtp}
                        autoFocus
                      />
                      <div className="otp-verify-actions">
                        <button className="otp-verify-btn otp-verify-btn--cancel" onClick={cancelDeliveredOtpInput} disabled={verifyingOtp}>
                          <XCircle size={16} /><span>Cancel</span>
                        </button>
                        <button className="otp-verify-btn otp-verify-btn--confirm" onClick={() => handleMarkDelivered(order.order_id)} disabled={verifyingOtp || otpInput.length !== 4}>
                          {verifyingOtp ? <Loader size={16} className="spin" /> : <ShieldCheck size={16} />}<span>Verify & Deliver</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {isTerminal && (
                  <div className="order-completed-notice" style={currentStatus === 'cancelled' ? { color: '#6c757d', borderColor: '#6c757d' } : undefined}>
                    {currentStatus === 'delivered' ? <Home size={16} /> : <XCircle size={16} />}
                    <span>{currentStatus === 'delivered' ? 'Order delivered successfully' : 'Order cancelled'}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Load More */}
        {hasNext && (
          <div className="orders-load-more">
            <button className="user-profile-edit-btn" onClick={handleLoadMoreOrders} disabled={loadingMoreOrders}>
              {loadingMoreOrders ? <Loader size={16} className="spin" /> : <RefreshCw size={16} />}
              <span>Load More</span>
            </button>
            <span className="orders-count">{orders.length} of {ordersTotal} orders</span>
          </div>
        )}
        </>
      )}

      {error && orders.length > 0 && (
        <div className="kitchen-orders-error">
          <AlertTriangle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Order Detail Modal */}
      {detailOrderId && (
        <div className="order-detail-overlay" onClick={closeOrderDetail}>
          <div className="order-detail-modal" onClick={e => e.stopPropagation()}>
            <div className="order-detail-modal-header">
              <h4>Order #{detailOrderId} Details</h4>
              <button className="order-detail-close-btn" onClick={closeOrderDetail}>
                <X size={20} />
              </button>
            </div>

            {detailLoading && (
              <div className="user-profile-empty-state" style={{ padding: '3rem' }}>
                <div className="user-profile-loading-spinner" style={{ width: '36px', height: '36px', marginBottom: '1rem' }}></div>
                <p>Loading order details...</p>
              </div>
            )}

            {detailError && !detailLoading && (
              <div className="user-profile-empty-state" style={{ padding: '2rem' }}>
                <AlertTriangle size={40} />
                <p>{detailError}</p>
                <button className="user-profile-edit-btn" onClick={() => fetchOrderDetail(detailOrderId)} style={{ marginTop: '1rem' }}>
                  <RefreshCw size={16} /> Retry
                </button>
              </div>
            )}

            {detailData && !detailLoading && (
              <div className="order-detail-content">
                {/* Order Summary */}
                <div className="detail-section">
                  <div className="detail-section-title">
                    <Package size={16} />
                    <span>Order Summary</span>
                  </div>
                  <div className="order-details-grid">
                    <div className="order-detail">
                      <span className="detail-label">Status</span>
                      <span className="detail-value">
                        <span className="order-status-badge" style={{ backgroundColor: getStatusColor(detailData.order.order_status), color: 'white' }}>
                          {getStatusIcon(detailData.order.order_status)}
                          <span>{detailData.order.order_status.replace('_', ' ')}</span>
                        </span>
                      </span>
                    </div>
                    <div className="order-detail">
                      <span className="detail-label">Delivery Type</span>
                      <span className="detail-value">{detailData.order.delivery_type === 'self_pickup' ? 'Self Pickup' : 'Kitchen Delivery'}</span>
                    </div>
                    <div className="order-detail">
                      <span className="detail-label">Payment</span>
                      <span className="detail-value">{detailData.order.payment_method} ({detailData.order.payment_status})</span>
                    </div>
                    <div className="order-detail">
                      <span className="detail-label">Subtotal</span>
                      <span className="detail-value">£{detailData.order.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="order-detail">
                      <span className="detail-label">Delivery Fee</span>
                      <span className="detail-value">£{detailData.order.delivery_fee.toFixed(2)}</span>
                    </div>
                    <div className="order-detail">
                      <span className="detail-label">Tax</span>
                      <span className="detail-value">£{detailData.order.tax_amount.toFixed(2)}</span>
                    </div>
                    <div className="order-detail">
                      <span className="detail-label">Total</span>
                      <span className="detail-value price">£{detailData.order.total_price.toFixed(2)}</span>
                    </div>
                    <div className="order-detail">
                      <span className="detail-label">Placed</span>
                      <span className="detail-value" style={{ fontSize: '0.85rem' }}>{formatDate(detailData.order.created_at)}</span>
                    </div>
                  </div>

                  {/* OTP info */}
                  {detailData.order.otp_required && (
                    <div className="detail-otp-info">
                      <ShieldCheck size={16} />
                      <span>OTP verification required for delivery. {detailData.order.otp_attempts_remaining} attempt(s) remaining.</span>
                    </div>
                  )}
                </div>

                {/* Customer Info */}
                <div className="detail-section">
                  <div className="detail-section-title">
                    <User size={16} />
                    <span>Customer</span>
                  </div>
                  <div className="detail-customer-info">
                    <div><strong>Name:</strong> {detailData.customer.name}</div>
                    {detailData.customer.phone && <div><strong>Phone:</strong> {detailData.customer.phone}</div>}
                    <div><strong>Email:</strong> {detailData.customer.email}</div>
                  </div>
                </div>

                {/* Items */}
                <div className="detail-section">
                  <div className="detail-section-title">
                    <ChefHat size={16} />
                    <span>Items</span>
                  </div>
                  <div className="detail-items-list">
                    {detailData.items.map(item => (
                      <div key={item.food_id} className="detail-item-row">
                        <span className="detail-item-name">{item.food_name}</span>
                        <span className="detail-item-qty">×{item.quantity}</span>
                        <span className="detail-item-price">£{item.price.toFixed(2)}</span>
                        <span className="detail-item-subtotal">£{item.subtotal.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Delivery Info */}
                {detailData.order.delivery_type !== 'self_pickup' && (
                  <div className="detail-section">
                    <div className="detail-section-title">
                      <Truck size={16} />
                      <span>Delivery</span>
                    </div>
                    <div className="detail-delivery-info">
                      {detailData.delivery.address && (
                        <div className="detail-delivery-row">
                          <MapPin size={14} />
                          <span>{detailData.delivery.address}</span>
                        </div>
                      )}
                      {detailData.delivery.distance_km > 0 && (
                        <div className="detail-delivery-row">
                          <span>Distance: {detailData.delivery.distance_km.toFixed(1)} km ({detailData.delivery.distance_miles.toFixed(1)} miles)</span>
                        </div>
                      )}
                      {detailData.delivery.eta && (
                        <div className="detail-delivery-row">
                          <Clock size={14} />
                          <span>ETA: {detailData.delivery.eta}</span>
                        </div>
                      )}
                      {detailData.delivery.google_maps_directions && (
                        <a
                          href={detailData.delivery.google_maps_directions}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="detail-maps-link"
                        >
                          <ExternalLink size={14} />
                          Open in Google Maps
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Timeline */}
                <div className="detail-section">
                  <div className="detail-section-title">
                    <Clock size={16} />
                    <span>Timeline</span>
                  </div>
                  <div className="detail-timeline">
                    {detailData.timeline.map((entry, idx) => (
                      <div key={idx} className="timeline-entry">
                        <div className="timeline-dot" style={{ backgroundColor: getStatusColor(entry.status) }} />
                        <div className="timeline-content">
                          <span className="timeline-status">{entry.status.replace('_', ' ').charAt(0).toUpperCase() + entry.status.replace('_', ' ').slice(1)}</span>
                          <span className="timeline-message">{entry.message}</span>
                          <span className="timeline-time">{formatTimelineDate(entry.timestamp)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Available Actions */}
                {detailData.available_actions.length > 0 && !TERMINAL_STATUSES.includes(detailData.order.order_status.toLowerCase()) && (
                  <div className="detail-section">
                    <div className="detail-section-title">
                      <ShieldCheck size={16} />
                      <span>Actions</span>
                    </div>
                    <div className="detail-actions">
                      {detailData.available_actions.map(action => {
                        const isMarkDelivered = action.endpoint.includes('mark-delivered');
                        return (
                          <button
                            key={action.action}
                            className="status-update-btn"
                            onClick={() => isMarkDelivered
                              ? openDeliveredOtpInput(detailData.order.order_id)
                              : handleActionEndpoint(action, detailData.order.order_id)}
                            disabled={updatingOrderId === detailData.order.order_id || (isMarkDelivered && markingDeliveredOrderId === detailData.order.order_id)}
                            style={{
                              backgroundColor: action.action === 'cancel' ? '#dc3545' : '#ff6b35',
                              color: 'white',
                            }}
                          >
                            {updatingOrderId === detailData.order.order_id ? <Loader size={16} className="spin" /> : isMarkDelivered ? <ShieldCheck size={16} /> : null}
                            <span>{action.label}</span>
                          </button>
                        );
                      })}
                    </div>

                    {markingDeliveredOrderId === detailData.order.order_id && (
                      <div className="otp-verify-section" style={{ marginTop: '1rem' }}>
                        <div className="otp-verify-header">
                          <ShieldCheck size={18} />
                          <span className="otp-verify-title">Verify Delivery OTP</span>
                        </div>
                        <p className="otp-verify-prompt">Ask the customer for their 4-digit OTP and enter it below to confirm delivery.</p>
                        <div className="otp-verify-body">
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength={4}
                            className="otp-verify-input"
                            placeholder="••••"
                            value={otpInput}
                            onChange={e => setOtpInput(e.target.value.replace(/\D/g, '').slice(0, 4))}
                            disabled={verifyingOtp}
                            autoFocus
                          />
                          <div className="otp-verify-actions">
                            <button className="otp-verify-btn otp-verify-btn--cancel" onClick={cancelDeliveredOtpInput} disabled={verifyingOtp}>
                              <XCircle size={16} /><span>Cancel</span>
                            </button>
                            <button className="otp-verify-btn otp-verify-btn--confirm" onClick={() => handleMarkDelivered(detailData.order.order_id)} disabled={verifyingOtp || otpInput.length !== 4}>
                              {verifyingOtp ? <Loader size={16} className="spin" /> : <ShieldCheck size={16} />}<span>Verify & Deliver</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MyKitchenOrdersTab;
